/**
 * Detects that a newer frontend build has been published and reloads to it.
 *
 * Wrapper-only: in a normal browser this installs no listeners, fetches no
 * manifest and never reloads.
 *
 * The reload is automatic and unprompted, so in-progress unsaved input can be
 * lost. That is the accepted trade-off for this behaviour.
 */
import {
  isNewerVersionAvailable,
  readLoadedAssetVersion,
  readPublishedAssetVersion,
  versionKey,
} from "./loadedAssetVersion";
import { isRunningInPlaintrWrapper } from "./wrapperEnvironment";

const MANIFEST_URL = "/asset-manifest.json";

/** Foreground events arrive in bursts; never check more often than this. */
const MIN_CHECK_INTERVAL_MS = 60_000;

const REQUEST_TIMEOUT_MS = 8_000;

/**
 * Records the version a reload was already attempted for. Kept in
 * `sessionStorage` because an in-memory flag dies with the reload it is meant to
 * guard: if the reload is answered with the same old HTML, the fresh document
 * would detect the same mismatch and reload again. Session scope means at most
 * one reload attempt per version per app session.
 */
const RELOAD_ATTEMPT_KEY = "plaintr.appUpdate.reloadAttemptedFor";

let lastCheckAt = 0;
let checkInFlight = false;
let installed = false;

function readReloadAttemptedFor() {
  try {
    return window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
  } catch {
    return null;
  }
}

/** False when the guard could not be persisted, in which case we must not reload. */
function recordReloadAttemptFor(key) {
  try {
    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, key);
    return window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY) === key;
  } catch {
    return false;
  }
}

async function fetchPublishedManifest() {
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timer =
    controller === null ? null : setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(MANIFEST_URL, {
      // Never let the check itself be answered from the cache being diagnosed.
      cache: "no-store",
      credentials: "same-origin",
      ...(controller === null ? {} : { signal: controller.signal }),
    });
    if (!response.ok) {
      return null;
    }
    // The distribution rescues a missing object as index.html with HTTP 200, so
    // a 200 here can still be HTML.
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("json")) {
      return null;
    }
    return await response.json();
  } catch {
    return null;
  } finally {
    if (timer !== null) {
      clearTimeout(timer);
    }
  }
}

/**
 * Run one check, reloading when a different published version is found.
 *
 * @returns {Promise<"skipped-not-wrapper"|"skipped-in-flight"|"skipped-rate-limited"|"skipped-hidden"|"skipped-hidden-after-check"|"unknown-loaded-version"|"check-failed"|"up-to-date"|"reload-suppressed-loop-guard"|"reload-suppressed-no-guard-storage"|"reloading">}
 */
export async function checkForWebAppUpdate({ force = false } = {}) {
  if (!isRunningInPlaintrWrapper()) {
    return "skipped-not-wrapper";
  }
  if (checkInFlight) {
    return "skipped-in-flight";
  }
  const now = Date.now();
  if (!force && now - lastCheckAt < MIN_CHECK_INTERVAL_MS) {
    return "skipped-rate-limited";
  }
  // Only ever reload a foreground app.
  if (document.visibilityState !== "visible") {
    return "skipped-hidden";
  }

  const loaded = readLoadedAssetVersion();
  if (loaded === null) {
    return "unknown-loaded-version";
  }

  checkInFlight = true;
  const previousCheckAt = lastCheckAt;
  lastCheckAt = now;
  try {
    const manifest = await fetchPublishedManifest();
    if (manifest === null) {
      // Offline or unreadable: keep the current screen.
      return "check-failed";
    }
    const published = readPublishedAssetVersion(manifest);
    if (!isNewerVersionAvailable(loaded, published)) {
      return "up-to-date";
    }

    // The app may have gone to the background while the request was in flight.
    // Re-check before consuming the one attempt for this version: roll the rate
    // limiter back so returning to the foreground can retry immediately.
    if (document.visibilityState !== "visible") {
      lastCheckAt = previousCheckAt;
      return "skipped-hidden-after-check";
    }

    const publishedKey = versionKey(published);
    if (readReloadAttemptedFor() === publishedKey) {
      // Already reloaded for this version and still not running it: stop here
      // instead of looping.
      return "reload-suppressed-loop-guard";
    }
    if (!recordReloadAttemptFor(publishedKey)) {
      return "reload-suppressed-no-guard-storage";
    }

    // Plain reload: no cache, cookie or storage is cleared here.
    window.location.reload();
    return "reloading";
  } finally {
    checkInFlight = false;
  }
}

/**
 * Wire the checker up: once at startup, and again on return to the foreground.
 * No-op outside the wrapper. Idempotent.
 *
 * @returns {() => void} teardown
 */
export function installWebAppUpdateChecker() {
  if (installed || typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }
  if (!isRunningInPlaintrWrapper()) {
    return () => {};
  }
  installed = true;

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void checkForWebAppUpdate();
    }
  };

  // `visibilitychange` is used instead of a Capacitor App event because the
  // remote document has no Capacitor bridge.
  document.addEventListener("visibilitychange", onVisibilityChange);
  void checkForWebAppUpdate({ force: true });

  return () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    installed = false;
  };
}

/** Test-only reset of module state. */
export function resetWebAppUpdateCheckerForTests() {
  lastCheckAt = 0;
  checkInFlight = false;
  installed = false;
}
